import { useContext, useEffect, useState } from 'react'
import Resizer from 'react-image-file-resizer'
import { useSelector } from '@xstate/react'
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import { LmnContext } from 'src/components/Dialogs/Lmn/LmnDialog'
import LmnBackButton from 'src/components/Dialogs/Lmn/common/LmnBackButton'
import { CDN_PATH } from 'src/constants/global'
import { getCustomerImage } from 'src/helpers/lmnHelpers'
import styles from 'src/components/Lmn/Lmn.scss'
import mixpanel from 'mixpanel-browser'

const TIPS_FOR_TAKING_PHOTO = [
  'Please do not upload a selfie.',
  'Your photo ID is well-lit and on a simple background.',
  'Capture all four corners of your photo ID.',
  'Nothing is obstructing your photo ID.',
]

const LmnIdentityVerification = (): JSX.Element => {
  const { breakpoints } = useTheme()
  const isSmallViewport = useMediaQuery(breakpoints.down('sm'))
  const { lmnContextService } = useContext(LmnContext)
  const { send } = lmnContextService
  const customerImage = useSelector(lmnContextService, getCustomerImage)
  const imageUrl = customerImage?.data ?? ''

  const [isImageLoading, setImageLoading] = useState(false)
  const [uploadError, setUploadError] = useState('')

  useEffect(() => {
    mixpanel.track('view_lmn_id_verification')
  }, [])

  const resizeFile = (file: File): void => {
    setImageLoading(true)

    try {
      Resizer.imageFileResizer(
        file,
        1000,
        1000,
        'JPEG',
        80,
        0,
        (resizedImage: any) => {
          setImageLoading(false)
          setUploadError('')
          send({
            type: 'SET_CUSTOMER_IMAGE',
            data: {
              customerImage: {
                data: String(resizedImage),
                mime: file.type,
              },
            },
          })
        },
        'base64',
      )
    } catch {
      setUploadError(`Error occurred reading file: ${file.name}`)
      setImageLoading(false)
    }
  }

  // eslint-disable-next-line
  const handleFileUpload = (event: any): void => {
    const file = event.target.files[0]

    if (!file.type.match('image/jpeg') && !file.type.match('image/png')) {
      setUploadError('Uploaded image must have a JPEG/PNG format')
      return
    }

    if (file.size > 20 * 1024 * 1024) {
      setUploadError('Uploaded image must be less than 20 MB')
      return
    }

    resizeFile(file)
  }

  const handleRetakePhoto = (): void => {
    setUploadError('')

    send({
      type: 'SET_CUSTOMER_IMAGE',
      data: {
        customerImage: null,
      },
    })
  }

  const imageUrlIsMissing = !imageUrl

  const handleNextStepClicked = (): void => {
    // TODO: need validation that everything is done before it does anything

    if (imageUrlIsMissing) {
      setUploadError('Please upload a photo of your ID.')
      return
    }

    setUploadError('')
    mixpanel.track('completed_lmn_id_verification')
    send('SUBMIT_LMN_FORM')
  }

  return (
    <>
      <Box display="flex" flexDirection="column" alignItems="center" gap="10px">
        <Typography variant="h4Bold">Verify your Idenity</Typography>
        <Typography variant="body3Bold">
          Your photo ID must match your HSA/FSA account.
        </Typography>
        <Box
          display="flex"
          gap="20px"
          borderRadius="5px"
          padding="20px"
          style={{
            background: 'honeydew',
          }}
        >
          <img
            className={styles.discoveryDialogItemIcon}
            src={`${CDN_PATH}/images/asset-lmn-identity-verification.svg`}
            alt="Id Icon"
          />
          <Typography variant="body3">
            For your security, telemedicine laws require us to verify the
            identity of our patients with a photo ID.
          </Typography>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" paddingTop="24px">
        {imageUrl && (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="270px"
              overflow="hidden"
            >
              <img className="sentry-mask" src={imageUrl} alt="Upload" height="100%" />
            </Box>
            <Button
              className={styles.lmnButton}
              startIcon={<RefreshIcon />}
              fullWidth
              size="large"
              variant="outlined"
              onClick={handleRetakePhoto}
            >
              <Typography variant="body2Bold">Update</Typography>
            </Button>
          </>
        )}
        {!imageUrl && (isImageLoading ? (
          <>
            <Button
              color="gray6"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '78px 0',
              }}
              component="label"
              fullWidth
              size="large"
              variant="contained"
            >
              <CircularProgress
                sx={{
                  '@keyframes layered': {
                    '0%': {
                      stroke: '#84EF84',
                    },
                    '50%': {
                      stroke: '#E2FAE3',
                    },
                    '100%': {
                      stroke: '#84EF84',
                    },
                  },
                  '& .MuiCircularProgress-circle': {
                    animation: 'layered 3s linear infinite',
                  },
                }}
                size={80}
              />
            </Button>
            {uploadError && (
              <Typography variant="body3" color="error.main" marginTop="10px">
                {uploadError}
              </Typography>
            )}
          </>
        ) : (
          <>
            <label htmlFor="upload-image">
              <Button
                color="gray6"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '78px 0',
                }}
                component="label"
                fullWidth
                size="large"
                variant="contained"
              >
                <Box paddingBottom="10px">
                  <img
                    src={`${CDN_PATH}/images/asset-lmn-upload-icon.svg`}
                    alt="Upload Icon"
                    width={45}
                  />
                </Box>
                <Typography variant="body2Bold">
                  Upload a photo of your ID
                </Typography>
                {isSmallViewport ? (
                  <Typography
                    variant="body2"
                    style={{
                      textDecoration: 'underline',
                    }}
                  >
                    or take a photo
                  </Typography>
                ) : null}
                <input
                  id="upload-image"
                  hidden
                  accept="image/jpeg, image/png"
                  multiple
                  type="file"
                  onChange={handleFileUpload}
                />
              </Button>
            </label>
            {uploadError && (
              <Typography variant="body3" color="error.main" marginTop="10px">
                {uploadError}
              </Typography>
            )}
          </>
        ))}

        <p style={{ fontSize: '12px', margin: '0px', marginTop: '10px' }}><b> Photos of anything other than a valid government-issued identification card will not be accepted.</b></p>

        <Box marginTop="24px">
          <Typography variant="body2Bold">
            Tips for taking a good photo:
          </Typography>
          <List
            sx={{
              paddingTop: '10px',
              paddingBottom: '14px',
            }}
          >
            {TIPS_FOR_TAKING_PHOTO.map((tip) => (
              <ListItem
                disablePadding
                sx={{
                  '.MuiListItemIcon-root': {
                    minWidth: '16px',
                  },
                }}
                key={tip}
              >
                <ListItemIcon>
                  <TaskAltIcon
                    color="primary"
                    sx={{
                      width: '16px',
                      margin: '4px',
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography variant="body2">{tip}</Typography>}
                />
              </ListItem>
            ))}
          </List>
        </Box>
        <Button
          className={styles.lmnButton}
          color="primary"
          fullWidth
          size="large"
          variant="contained"
          onClick={handleNextStepClicked}
        >
          <Typography variant="body2Bold">Continue</Typography>
        </Button>
        <LmnBackButton />
      </Box>
    </>
  )
}

export default LmnIdentityVerification
